import React, { useEffect, useRef } from 'react';
import { Ionicons } from '@expo/vector-icons';
import {
  METHOD_ICONS, METHOD_LABELS, Passage, REASON_LABELS, RESULT_LABELS, SOURCE_LABELS,
  ago, dayLabel, subjectOf, timeOf,
} from './sentinel';

type Props = {
  passages: Passage[];
  selected: string | null;
  fresh: Set<string>;
  loading: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  now: number;
  filtered: boolean;
  onLoadMore: () => void;
  onSelect: (uuid: string) => void;
  header: React.ReactNode;
};

/** La bitácora: intentos de paso como línea de tiempo, agrupados por día. */
export default function PassageFeed(props: Props) {
  const { passages, selected, fresh, loading, hasMore, loadingMore, now, filtered, onLoadMore, onSelect, header } = props;
  const sentinel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasMore) return;
    const io = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && !loadingMore) onLoadMore();
    }, { rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loadingMore, onLoadMore]);

  let lastDay = '';

  return (
    <div className="cc-col cc-feed">
      {header}
      <div className="cc-scroll">
        {loading && [0, 1, 2, 3, 4, 5].map(i => <div key={i} className="cc-skel" style={{ opacity: 1 - i * 0.14 }} />)}

        {!loading && passages.length === 0 && (
          <div className="cc-empty">
            <div className="cc-empty-radar"><span /><span /><span /><b /></div>
            <div className="cc-display" style={{ fontSize: 16, color: 'var(--text)' }}>
              {filtered ? 'Nada con estos filtros' : 'Todo tranquilo'}
            </div>
            <div style={{ fontSize: 13, marginTop: 4 }}>
              {filtered ? 'Prueba con otro periodo o quita algún filtro.' : 'Los accesos aparecen aquí en cuanto suceden.'}
            </div>
          </div>
        )}

        {passages.map(p => {
          const day = dayLabel(p.occurred_at);
          const showDay = day !== lastDay;
          lastDay = day;
          return (
            <React.Fragment key={p.uuid}>
              {showDay && <div className="cc-day">{day}</div>}
              <PassageRow
                passage={p}
                selected={selected === p.uuid}
                fresh={fresh.has(p.uuid)}
                now={now}
                onSelect={onSelect}
              />
            </React.Fragment>
          );
        })}

        {hasMore && <div ref={sentinel} className="cc-loadmore" />}
        {loadingMore && <div className="cc-skel" />}
      </div>
    </div>
  );
}

function PassageRow({ passage: p, selected, fresh, now, onSelect }: {
  passage: Passage; selected: boolean; fresh: boolean; now: number; onSelect: (uuid: string) => void;
}) {
  const who = subjectOf(p);
  const where = [p.access_point?.name, p.zone?.name].filter(Boolean).join(' · ') || SOURCE_LABELS[p.source];
  const openFor = p.open_until ? new Date(p.open_until).getTime() - now : 0;

  return (
    <button
      className={`cc-row${selected ? ' selected' : ''}${fresh ? ` fresh ${p.result}` : ''}`}
      onClick={() => onSelect(p.uuid)}
    >
      <div className="cc-time cc-num">
        {timeOf(p.occurred_at)}
        <small>{ago(p.occurred_at, now)}</small>
      </div>
      <div className="cc-node"><i className={p.result} /></div>
      <div className="cc-who">
        <b>
          {who.title}
          {p.flags?.includes('delinquent') && (
            <span className="cc-flag" title={p.details?.debt ? `${p.details.debt.overdue_fees} cuotas vencidas` : undefined}>
              Moroso{p.details?.debt ? ` · ${p.details.debt.overdue_fees}` : ''}
            </span>
          )}
        </b>
        <span>{[who.subtitle, where].filter(Boolean).join('  ·  ')}</span>
      </div>
      <div className="cc-tags">
        {p.methods.slice(0, 3).map(m => (
          <span key={m} className="cc-method" title={METHOD_LABELS[m] ?? m}>
            <Ionicons name={(METHOD_ICONS[m] ?? 'ellipse-outline') as any} size={14} color="currentColor" />
          </span>
        ))}
        <span className="cc-dir">{p.direction === 'entry' ? 'Entrada' : p.direction === 'exit' ? 'Salida' : ''}</span>
        <span className={`cc-badge ${p.result}`}>
          {openFor > 0 && <OpenRing passage={p} now={now} />}
          {p.result === 'denied' && p.reason ? REASON_LABELS[p.reason] ?? p.reason : RESULT_LABELS[p.result]}
        </span>
      </div>
    </button>
  );
}

/** Reloj de la ventana de correlación: mientras gira, el intento todavía puede recibir señales. */
function OpenRing({ passage, now }: { passage: Passage; now: number }) {
  const start = new Date(passage.occurred_at).getTime();
  const total = new Date(passage.open_until!).getTime() - start;
  const elapsed = Math.max(0, now - start);

  return (
    <svg className="cc-ring" viewBox="0 0 16 16" aria-label="Recibiendo señales">
      <circle className="track" cx="8" cy="8" r="7" />
      <circle
        className="left"
        cx="8" cy="8" r="7"
        style={{ animationDuration: `${total}ms`, animationDelay: `-${elapsed}ms` }}
      />
    </svg>
  );
}
